import dayjs from "dayjs";
import {
  PRESSURE_ITP_TYPES,
  PIPING_ITP_TYPES,
  normalizeItpTypeValue,
} from "./constants";import {
  getParentTaskCode,
  isThreeLevelTaskCode,
  parseTaskCode,
} from "./taskCodeUtils";

export const getItpValue = (row, keys) => {
  if (!row || typeof row !== "object") return "";

  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
      return row[key];
    }
  }

  return "";
};

export const parseNumberValue = (value) => {
  if (value === undefined || value === null || value === "") return 0;

  const number = Number(String(value).replace("%", "").trim());

  return Number.isNaN(number) ? 0 : number;
};

export const getItpRowId = (row) => {
  return getItpValue(row, ["id", "_id", "itpId", "ITP_ID"]);
};

export const getItpRowTagNumber = (row) => {
  return getItpValue(row, ["tagNumber", "Tag Number", "tag_number"]);
};

export const getTaskValueForTagNumber = (task) => {
  return (
    task?.task ||
    task?.name ||
    parseTaskCode(task?.taskCode || "").groupName ||
    task?.taskCode ||
    ""
  );
};

export const buildThreeLevelTaskValueMap = (tasks) => {
  const map = new Map();
  const taskRows = Array.isArray(tasks) ? tasks : [];

  taskRows.forEach((task) => {
    const type = String(task?.type || "").toLowerCase();

    if (type !== "itp") return;
    if (!isThreeLevelTaskCode(task?.taskCode)) return;

    const parentCode = getParentTaskCode(task?.taskCode);
    const taskValue = getTaskValueForTagNumber(task);

    if (!parentCode || !taskValue) return;

    if (!map.has(parentCode)) {
      map.set(parentCode, []);
    }

    map.get(parentCode).push({
      taskId: task?.id,
      taskCode: task?.taskCode,
      taskValue,
    });
  });

  return map;
};

export const getItpRowsForTask = (task, itpTable) => {
  const rows = Array.isArray(itpTable) ? itpTable : [];

  const taskText = String(
    task?.task || task?.name || task?.taskCode || ""
  ).toLowerCase();

  let allowedTypes = null;

  if (taskText.includes("pressure")) {
    allowedTypes = PRESSURE_ITP_TYPES;
  } else if (taskText.includes("piping")) {
    allowedTypes = PIPING_ITP_TYPES;
  }

  if (!allowedTypes) {
    return rows;
  }

  return rows.filter((row) => {
    const rowType = getItpValue(row, [
      "type",
      "Type",
      "itpType",
      "ITP Type",
      "equipmentType",
      "equipment_type",
    ]);

    return allowedTypes.includes(normalizeItpTypeValue(rowType));
  });
};

export const getOldestDateValue = (rows) => {
  const validDates = rows
    .map((row) =>
      getItpValue(row, [
        "Start Date",
        "startDate",
        "start_date",
        "plannedStart",
      ])
    )
    .filter(Boolean)
    .map((value) => ({
      originalValue: value,
      dateValue: dayjs(value),
    }))
    .filter((item) => item.dateValue.isValid());

  if (validDates.length === 0) return "";

  validDates.sort((a, b) => a.dateValue.valueOf() - b.dateValue.valueOf());

  return validDates[0].originalValue;
};

export const getNewestEndDateValue = (rows) => {
  const validDates = rows
    .map((row) =>
      getItpValue(row, [
        "End Date",
        "endDate",
        "end_date",
        "plannedEnd",
        "actualEnd",
      ])
    )
    .filter(Boolean)
    .map((value) => ({
      originalValue: value,
      dateValue: dayjs(value),
    }))
    .filter((item) => item.dateValue.isValid());

  if (validDates.length === 0) return "";

  validDates.sort((a, b) => b.dateValue.valueOf() - a.dateValue.valueOf());

  return validDates[0].originalValue;
};

export const buildItpSummary = (itpTable) => {
  const rows = Array.isArray(itpTable) ? itpTable : [];

  if (rows.length === 0) {
    return {
      startDate: "",
      endDate: "",
      averageProgress: 0,
      totalActualHours: 0,
    };
  }

  const oldestStartDate = getOldestDateValue(rows);
  const newestEndDate = getNewestEndDateValue(rows);

  const progressValues = rows.map((row) =>
    parseNumberValue(getItpValue(row, ["Progress", "progress"]))
  );

  const actualHoursValues = rows.map((row) =>
    parseNumberValue(
      getItpValue(row, [
        "Actual Hrs",
        "Actual Hours",
        "actualHrs",
        "actualHours",
        "hours",
        "manhours",
      ])
    )
  );

  const totalProgress = progressValues.reduce((sum, value) => sum + value, 0);
  const totalActualHours = actualHoursValues.reduce(
    (sum, value) => sum + value,
    0
  );

  return {
    startDate: oldestStartDate,
    endDate: newestEndDate,
    averageProgress: rows.length ? totalProgress / rows.length : 0,
    totalActualHours,
  };
};

export const applySameItpSummaryToTask = (task, summaryByParentCode) => {
  const parentCode = getParentTaskCode(task?.taskCode);
  const itpSummary = summaryByParentCode.get(parentCode);

  if (!itpSummary) return task;

  return {
    ...task,

    progress: itpSummary.averageProgress,
    manhours: itpSummary.totalActualHours,

    actualStart: itpSummary.startDate || task.actualStart,
    plannedStart: itpSummary.startDate || task.plannedStart,

    actualEnd: itpSummary.endDate || task.actualEnd,
    plannedEnd: itpSummary.endDate || task.plannedEnd,
    actualFinish: itpSummary.endDate || task.actualFinish,

    actualHrDollar: itpSummary.totalActualHours,
    actualHours: itpSummary.totalActualHours,
    hours: itpSummary.totalActualHours,

    __itpSummaryApplied: true,
  };
};
